import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { ArrowLeft, Calendar, MessageSquare, Hammer, AlertCircle, Bell, ChevronLeft, ChevronRight } from 'lucide-react';

const GET_MY_EVENTS = gql`
  query MyEvents($limit: Int, $offset: Int) {
    currentUser {
      domoreEvents(limit: $limit, offset: $offset) {
        totalCount
        items {
          id
          eventType
          eventDatetime
          objectType
          objectId
          eventText
        }
      }
    }
  }
`;

const History: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const { data, loading, error } = useQuery(GET_MY_EVENTS, {
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
    },
    fetchPolicy: 'network-only',
  });

  const events = data?.currentUser?.domoreEvents?.items || [];
  const totalCount = data?.currentUser?.domoreEvents?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEventIconConfig = (eventType: string) => {
    const normalizedType = (eventType || '').toLowerCase();

    if (normalizedType.includes('message')) {
      return {
        icon: MessageSquare,
        containerClass: 'bg-green-100 text-green-600',
      };
    }

    if (normalizedType.includes('return')) {
      return {
        icon: Hammer,
        containerClass: 'bg-purple-100 text-purple-600',
      };
    }

    if (normalizedType.includes('request')) {
      return {
        icon: AlertCircle,
        containerClass: 'bg-orange-100 text-orange-600',
      };
    }

    if (normalizedType.includes('booking') || normalizedType.includes('book')) {
      return {
        icon: Calendar,
        containerClass: 'bg-blue-100 text-blue-600',
      };
    }

    return {
      icon: Bell,
      containerClass: 'bg-gray-100 text-gray-600',
    };
  };

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til dashboard</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Historik</h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Indlæser aktivitet...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">Der opstod en fejl ved indlæsning af historik.</p>
          </div>
        ) : events.length > 0 ? (
          <>
            <div className="space-y-6">
              {events.map((event: any) => {
                const iconConfig = getEventIconConfig(event.eventType);
                const EventIcon = iconConfig.icon;

                return (
                  <div key={event.id} className="flex">
                    <div className="mr-4">
                      <div className={`${iconConfig.containerClass} rounded-full p-2`}>
                        <EventIcon className="h-5 w-5" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-900">{event.eventText}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(event.eventDatetime)}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Viser {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} af {totalCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-700">
                    Side {currentPage} af {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-md border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Ingen historik endnu</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;